import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBackups,
  getVMBackups,
  createBackup,
  restoreBackup,
  getRetentionPolicies,
  updateRetentionPolicy,
} from "@/api/endpoints/backups";
import type { BackupCreatePayload, RestorePayload, RetentionPolicy } from "@/types";
import { useActiveTenant } from "@/components/tenant/TenantSelector";

export function useBackups() {
  const [tenantId] = useActiveTenant();

  return useQuery({
    queryKey: ["backups", tenantId],
    queryFn: () => getBackups(tenantId ?? undefined),
    enabled: !!tenantId,
  });
}

export function useVMBackups(vmId: number) {
  return useQuery({
    queryKey: ["vm-backups", vmId],
    queryFn: () => getVMBackups(vmId),
    enabled: vmId > 0,
  });
}

export function useCreateBackup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BackupCreatePayload) => createBackup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backups"] });
    },
  });
}

export function useRestoreBackup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ backupId, ...payload }: { backupId: number } & RestorePayload) =>
      restoreBackup(backupId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backups"] });
      queryClient.invalidateQueries({ queryKey: ["vms"] });
    },
  });
}

export function useRetentionPolicies(tenantId: number | null) {
  return useQuery({
    queryKey: ["retention-policies", tenantId],
    queryFn: () => getRetentionPolicies(tenantId!),
    enabled: !!tenantId,
  });
}

export function useUpdateRetentionPolicy() {
  const queryClient = useQueryClient();
  const [tenantId] = useActiveTenant();

  return useMutation({
    mutationFn: (policy: Partial<RetentionPolicy>) =>
      updateRetentionPolicy(tenantId!, policy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["retention-policies"] });
    },
  });
}
